import axios from "./httpClient";
import * as _ from "lodash";

import { ApiType } from "./Types";
import { getUrl } from "./getURL";
import { ErrorMessages, STORAGE_KEYS } from "../constants";
import { getTrackingHeadersMap } from "./getTrackingHeaders";
import { IAuthUser } from "../models/user";

// Increased retries to handle post-callback token storage timing
const MAX_TOKEN_FETCH_RETRIES = 3;
const MAX_VALIDATION_RETRIES = 0; // Single attempt - errors handled by interceptor
const RETRY_DELAY_MS = 1000; // Delay between retries in milliseconds
const TOKEN_RETRY_DELAY_MS = 300; // 300ms delay between token fetch attempts

/**
 * GET API call using Axios with retry mechanism and delay between retries.
 * If the token is empty, it will retry fetching the token with a delay.
 * @returns - Logged in User details type of IAuthUser
 */
export const validateOktaToken = async (): Promise<IAuthUser> => {
  const token = await fetchTokenWithRetry();
  return validateTokenWithRetry(token);
};

// Helper function to retry fetching the token
const fetchTokenWithRetry = async (): Promise<string> => {
  let tokenAttempts = 0;

  while (tokenAttempts < MAX_TOKEN_FETCH_RETRIES) {
    const user = JSON.parse(localStorage.getItem(STORAGE_KEYS.OKTA_TOKEN_STORAGE) ?? "null");
    const token = _.get(user, "idToken.idToken");

    if (token) {
      console.log(`Okta token retrieved on attempt ${tokenAttempts + 1}`);
      return token;
    }

    tokenAttempts++;
    if (tokenAttempts < MAX_TOKEN_FETCH_RETRIES) {
      console.warn(
        `Token not found in storage (attempt ${tokenAttempts}/${MAX_TOKEN_FETCH_RETRIES}). Retrying in ${TOKEN_RETRY_DELAY_MS}ms...`
      );
      await delay(TOKEN_RETRY_DELAY_MS);
    }
  }

  throw new Error("Failed to retrieve Okta token from storage after multiple attempts.");
};

// Helper function to validate token with retry mechanism
const validateTokenWithRetry = async (token: string): Promise<IAuthUser> => {
  let attempts = 0;

  // Always make at least one attempt, plus any retries
  while (attempts <= MAX_VALIDATION_RETRIES) {
    try {
      const staticHeaders = getTrackingHeadersMap(ApiType.VALIDATE_OKTA_TOKEN);
      const headers = {
        ...staticHeaders,
      };
      const response = await axios.post(
        getUrl(ApiType.VALIDATE_OKTA_TOKEN),
        { token },
        { headers }
      );
      return response.data;
    } catch (error) {
      attempts++;
      if (attempts > MAX_VALIDATION_RETRIES) {
        console.error("Maximum validation retry attempts reached.");
        throw new Error(getErrorMessage(error));
      }

      console.warn(
        `Validation attempt ${attempts} failed. Retrying in ${RETRY_DELAY_MS}ms...`
      );
      await delay(RETRY_DELAY_MS);
    }
  }

  throw new Error(ErrorMessages.validateOktaToken);
};

// Extracted error handling logic
const getErrorMessage = (error: any): string => {
  const status = _.get(error, "response.status");
  if (!status) throw new Error(ErrorMessages.statusCodeNotFound);
  if (status === 503) throw new Error(ErrorMessages.deploymentInprogressError);
  if (status === 500) return ErrorMessages.serverError;
  return (
    _.get(error, "response.data.error.message") ??
    _.get(error, "response.data.errors[0].message") ??
    ErrorMessages.validateOktaToken
  );
};

// Generic delay function
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
